import { getBackendUrl } from '../../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft, Download, Loader2, MapPin, Clock, Award, Briefcase, Star,
  User, GraduationCap, Wrench, Languages, Mail, Phone, Globe,
  TrendingUp, TrendingDown, Minus, CheckCircle, Calendar, Edit, Heart,
  FileText, MessageSquare, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

// =====================
// SALARY BENCHMARK CARD
// =====================
const SalaryBenchmarkCard = ({ employee, employeeId }) => {
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBenchmark = async () => {
      if (!employeeId || !employee?.country) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await api.get(`/salary-benchmark/${employeeId}`);
        if (response.ok) {
          const data = await response.json();
          setBenchmark(data);
        }
      } catch (err) {
        console.error('Benchmark fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBenchmark();
  }, [employeeId, employee?.country, employee?.job_category, employee?.years_of_experience]);

  const hasSalaryExpectation = employee?.expected_salary_local || employee?.expected_salary_eur;

  if (loading) {
    return (
      <Card className="p-6 border-tp-border">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-tp-text-light" />
        </div>
      </Card>
    );
  }

  if (!hasSalaryExpectation && (!benchmark || !benchmark.data_available)) {
    return null;
  }

  const getStatusColor = (color) => {
    switch (color) {
      case 'green': return 'text-tp-success bg-tp-success/10 border-tp-success/30';
      case 'orange': return 'text-tp-warning bg-tp-warning/10 border-tp-warning/30';
      case 'red': return 'text-tp-error bg-tp-error/10 border-tp-error/30';
      default: return 'text-tp-text-light bg-tp-bg border-tp-border';
    }
  };

  return (
    <Card className="p-6 border-tp-border" data-testid="salary-benchmark-card">
      <h3 className="text-sm font-semibold text-tp-text mb-4 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-tp-accent" />
        Prétentions salariales
      </h3>

      {hasSalaryExpectation && (
        <div className="space-y-3">
          {employee.expected_salary_local && employee.currency && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-tp-text-light">Local</span>
              <span className="font-semibold text-tp-text">
                {employee.expected_salary_local.toLocaleString('fr-FR')} {employee.currency}
              </span>
            </div>
          )}
          {employee.expected_salary_eur && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-tp-text-light">EUR</span>
              <span className="font-semibold text-tp-primary">
                €{employee.expected_salary_eur.toLocaleString('fr-FR')}
              </span>
            </div>
          )}
        </div>
      )}

      {benchmark && benchmark.data_available && benchmark.position_label && (
        <div className={`mt-3 p-2 rounded text-xs text-center ${getStatusColor(benchmark.position_color)}`}>
          {benchmark.position_label}
        </div>
      )}
    </Card>
  );
};

// =====================
// MAIN COMPONENT
// =====================
const EmployeeProfilePage = () => {
  const { employeeId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchEmployee = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/talent-pool/${employeeId}`);
      const data = await response.json();
      setEmployee(data);
      setNotes(data.internal_notes || '');
    } catch (error) {
      console.error('Failed to fetch employee:', error);
      toast.error('Échec du chargement du profil');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const fetchRating = useCallback(async () => {
    try {
      const response = await api.get(`/admin/talents/${employeeId}/rating`);
      const data = await response.json();
      if (data.has_rating) {
        setRating(data);
      }
    } catch (error) {
      console.error('Failed to fetch rating:', error);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployee();
    fetchRating();
  }, [fetchEmployee, fetchRating]);

  const downloadCV = () => {
    window.open(
      `${getBackendUrl()}/api/admin/candidates/${employeeId}/cv`,
      '_blank'
    );
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const response = await api.patch(`/admin/candidates/${employeeId}`, {
        internal_notes: notes
      });
      if (response.ok) {
        toast.success('Notes sauvegardées');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingNotes(false);
    }
  };

  const getExperienceBadge = (years) => {
    if (!years) return { label: 'Non spécifié', class: 'bg-gray-400 text-white' };
    if (years <= 2) return { label: 'Junior', class: 'bg-level-junior text-white' };
    if (years <= 5) return { label: 'Intermédiaire', class: 'bg-level-confirme text-white' };
    return { label: 'Senior', class: 'bg-level-senior text-white' };
  };

  const getCategoryLabel = (categoryId) => {
    const categories = {
      'assistant_virtuel': 'Assistant Virtuel',
      'developpeur': 'Développeur',
      'marketing_digital': 'Marketing Digital',
      'design_graphique': 'Design Graphique',
      'comptabilite': 'Comptabilité',
      'rh': 'Ressources Humaines',
      'commercial': 'Commercial',
      'autre': 'Autre'
    };
    return categories[categoryId] || categoryId || 'Non spécifié';
  };

  const getFrenchLevelLabel = (level) => {
    const levels = {
      'native': 'Natif',
      'fluent': 'Courant',
      'intermediate': 'Intermédiaire',
      'basic': 'Basique'
    };
    return levels[level] || level;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-4 md:p-8">
        <p className="text-tp-text-light">Talent non trouvé</p>
      </div>
    );
  }

  const analysis = employee.ai_analysis;
  const expBadge = getExperienceBadge(employee.years_of_experience);

  return (
    <div className="p-4 md:p-8" data-testid="employee-profile-page">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/talent-pool" className="inline-flex items-center text-sm text-tp-text-light hover:text-tp-primary mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour au Talent Pool
        </Link>
        
        <div className="space-y-4">
          {/* Top: Avatar + Name */}
          <div className="flex items-start gap-3 md:gap-4">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-tp-accent flex items-center justify-center text-white text-lg md:text-xl font-display font-semibold flex-shrink-0">
              {employee.first_name?.[0]}{employee.last_name?.[0]}
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-xl md:text-2xl font-display font-semibold text-tp-text">
                  {employee.first_name} {employee.last_name}
                </h1>
                <Badge className={expBadge.class}>
                  {expBadge.label}
                </Badge>
              </div>
              <p className="text-tp-text-light text-sm mb-2">{getCategoryLabel(employee.job_category)}</p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-3 text-sm text-tp-text-light">
                <span className="flex items-center gap-1 truncate">
                  <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{employee.email}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                  {employee.phone}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                  {employee.city}, {employee.country}
                </span>
              </div>
            </div>
          </div>
          
          {/* Badges & Actions row */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              Disponible
            </Badge>
            
            {employee.cv_storage_path && (
              <Button variant="outline" size="sm" onClick={downloadCV} className="border-tp-border ml-auto" data-testid="download-cv-button">
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Télécharger</span> CV
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs - Same as Candidate Profile */}
      <Tabs defaultValue="fiche" className="space-y-4 md:space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="bg-tp-bg inline-flex w-auto min-w-full md:w-auto">
            <TabsTrigger value="fiche" className="text-xs sm:text-sm whitespace-nowrap">Fiche Talent</TabsTrigger>
            <TabsTrigger value="cv" className="text-xs sm:text-sm whitespace-nowrap">CV & Analyse</TabsTrigger>
            <TabsTrigger value="evaluations" className="text-xs sm:text-sm whitespace-nowrap">Évaluations</TabsTrigger>
            <TabsTrigger value="notes" className="text-xs sm:text-sm whitespace-nowrap">Notes</TabsTrigger>
          </TabsList>
        </div>

        {/* FICHE TALENT - Same structure as Candidate */}
        <TabsContent value="fiche" className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Résumé */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-lg font-display font-semibold text-tp-text">Résumé du profil</h3>
                  </div>
                </div>
                {analysis?.summary ? (
                  <p className="text-tp-text-light leading-relaxed">{analysis.summary}</p>
                ) : (
                  <p className="text-sm text-tp-text-light italic">Non renseigné - Cliquez sur l'icône pour ajouter</p>
                )}
              </Card>

              {/* Formation */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-lg font-display font-semibold text-tp-text">Formation</h3>
                  </div>
                </div>
                {analysis?.education?.length > 0 ? (
                  <div className="space-y-4">
                    {analysis.education.map((edu, index) => (
                      <div key={`edu-${edu.degree}-${index}`} className="border-l-2 border-tp-accent pl-4">
                        <p className="font-medium text-tp-text">{edu.degree}</p>
                        <p className="text-sm text-tp-text-light">{edu.institution}</p>
                        {edu.year && <p className="text-xs text-tp-text-light">{edu.year}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-tp-text-light italic">Non renseigné</p>
                )}
              </Card>

              {/* Expérience */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-lg font-display font-semibold text-tp-text">Expérience professionnelle</h3>
                  </div>
                  <Badge variant="secondary">
                    {employee.years_of_experience} ans
                  </Badge>
                </div>
                {analysis?.work_experience?.length > 0 ? (
                  <div className="space-y-4">
                    {analysis.work_experience.map((exp, index) => (
                      <div key={`exp-${exp.company}-${index}`} className="border-l-2 border-tp-primary-light pl-4">
                        <p className="font-medium text-tp-text">{exp.title}</p>
                        <p className="text-sm text-tp-primary-light font-medium">{exp.company}</p>
                        <p className="text-xs text-tp-text-light mb-2">{exp.duration}</p>
                        {exp.description && (
                          <p className="text-sm text-tp-text-light">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-tp-text-light italic">Non renseigné</p>
                )}
              </Card>

              {/* Compétences */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-lg font-display font-semibold text-tp-text">Compétences</h3>
                  </div>
                </div>
                {analysis?.skills?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {analysis.skills.map((skill, index) => (
                      <Badge key={`skill-${skill}`} className="bg-tp-accent/10 text-tp-accent border-0">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-tp-text-light italic">Non renseigné</p>
                )}
              </Card>
            </div>

            {/* Right Column - Quick Info (Same as Candidate) */}
            <div className="space-y-6">
              {/* Score */}
              {analysis?.candidate_score && (
                <Card className="p-6 border-tp-border">
                  <h3 className="text-sm font-semibold text-tp-text mb-4">Score candidat</h3>
                  <div className="text-center mb-4">
                    <div className={`text-4xl font-display font-bold ${
                      analysis.candidate_score >= 70 ? 'text-tp-success' :
                      analysis.candidate_score >= 50 ? 'text-tp-warning' :
                      'text-tp-error'
                    }`}>
                      {analysis.candidate_score}
                    </div>
                    <p className="text-xs text-tp-text-light">/ 100</p>
                  </div>
                  <Progress value={analysis.candidate_score} className="h-2 mb-2" />
                  {analysis.seniority_level && (
                    <p className="text-xs text-tp-text-light text-center capitalize">
                      {analysis.seniority_level}
                    </p>
                  )}
                </Card>
              )}

              {/* Langues */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Languages className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-sm font-semibold text-tp-text">Langues</h3>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-tp-text">Français</span>
                    <Badge variant="secondary" className="capitalize text-xs">
                      {getFrenchLevelLabel(employee.french_level)}
                    </Badge>
                  </div>
                  {analysis?.languages?.map((lang, index) => (
                    <div key={`lang-${lang.language}-${index}`} className="flex items-center justify-between">
                      <span className="text-sm text-tp-text">{lang.language}</span>
                      <Badge variant="secondary" className="text-xs">{lang.level}</Badge>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Outils maîtrisés */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-sm font-semibold text-tp-text">Outils maîtrisés</h3>
                  </div>
                </div>
                {analysis?.tools?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {analysis.tools.map((tool, index) => (
                      <Badge key={`tool-${tool}`} variant="outline" className="text-xs border-tp-border">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-tp-text-light italic">Non renseigné</p>
                )}
              </Card>

              {/* Points forts */}
              <Card className="p-6 border-tp-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-tp-accent" />
                    <h3 className="text-sm font-semibold text-tp-text">Points forts</h3>
                  </div>
                </div>
                {analysis?.strengths?.length > 0 ? (
                  <ul className="space-y-2">
                    {analysis.strengths.map((strength, index) => (
                      <li key={`strength-${strength}-${index}`} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-tp-success mt-0.5 flex-shrink-0" />
                        <span className="text-tp-text">{strength}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-tp-text-light italic">Non renseigné</p>
                )}
              </Card>

              {/* Prérequis techniques */}
              <Card className="p-6 border-tp-border">
                <h3 className="text-sm font-semibold text-tp-text mb-4">Prérequis techniques</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-tp-text-light">Ordinateur</span>
                    <span className={employee.has_computer ? 'text-tp-success' : 'text-tp-error'}>
                      {employee.has_computer ? '✓ Oui' : '✗ Non'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-tp-text-light">Internet stable</span>
                    <span className={employee.has_stable_internet ? 'text-tp-success' : 'text-tp-error'}>
                      {employee.has_stable_internet ? '✓ Oui' : '✗ Non'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-tp-text-light">Espace calme</span>
                    <span className={employee.has_quiet_workspace ? 'text-tp-success' : 'text-tp-error'}>
                      {employee.has_quiet_workspace ? '✓ Oui' : '✗ Non'}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Salary Benchmark */}
              <SalaryBenchmarkCard employee={employee} employeeId={employeeId} />
            </div>
          </div>
        </TabsContent>

        {/* CV & ANALYSE */}
        <TabsContent value="cv" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* CV Section */}
            <Card className="p-6 border-tp-border">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-tp-accent" />
                <h3 className="text-lg font-display font-semibold text-tp-text">Curriculum Vitae</h3>
              </div>
              {employee.cv_storage_path ? (
                <div className="space-y-4">
                  <p className="text-sm text-tp-text-light">CV disponible au téléchargement</p>
                  <Button onClick={downloadCV} className="w-full bg-tp-primary hover:bg-tp-primary-light">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger le CV
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-tp-text-light italic">Aucun CV uploadé</p>
              )}
            </Card>

            {/* AI Analysis Summary */}
            {analysis && (
              <Card className="p-6 border-tp-border">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="h-5 w-5 text-tp-accent" />
                  <h3 className="text-lg font-display font-semibold text-tp-text">Analyse IA</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-tp-text-light">Score global</span>
                    <span className="font-bold text-tp-primary">{analysis.candidate_score}/100</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-tp-text-light">Niveau</span>
                    <Badge variant="secondary">{analysis.seniority_level}</Badge>
                  </div>
                  {analysis.classification && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-tp-text-light">Classification</span>
                      <Badge variant="secondary">{analysis.classification}</Badge>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Detailed Skills */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 border-tp-border">
              <div className="flex items-center gap-2 mb-4">
                <Star className="h-5 w-5 text-tp-accent" />
                <h3 className="text-lg font-display font-semibold text-tp-text">Compétences détaillées</h3>
              </div>
              {analysis?.skills?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {analysis.skills.map((skill, index) => (
                    <Badge key={`skill-${skill}-${index}`} className="bg-tp-primary/10 text-tp-primary border-tp-primary/20">
                      {skill}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-tp-text-light italic">Non renseigné</p>
              )}
            </Card>

            <Card className="p-6 border-tp-border">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="h-5 w-5 text-tp-accent" />
                <h3 className="text-lg font-display font-semibold text-tp-text">Outils & Technologies</h3>
              </div>
              {analysis?.tools?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {analysis.tools.map((tool, index) => (
                    <Badge key={`skill2-${tool}-${index}`} className="bg-tp-accent/10 text-tp-accent border-tp-accent/20">
                      {tool}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-tp-text-light italic">Non renseigné</p>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* EVALUATIONS */}
        <TabsContent value="evaluations" className="space-y-6">
          {rating ? (
            <Card className="p-6 border-tp-border" data-testid="talent-rating-card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-display font-semibold text-tp-text">Évaluations clients</h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`h-5 w-5 ${
                          star <= Math.round(rating.average_score) 
                            ? 'fill-amber-400 text-amber-400' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-2xl font-display font-semibold text-tp-text">
                    {rating.average_score.toFixed(1)}
                  </span>
                  <span className="text-tp-text-light">/5</span>
                </div>
              </div>
              
              <p className="text-sm text-tp-text-light mb-6">
                Basé sur {rating.total_evaluations} évaluation{rating.total_evaluations > 1 ? 's' : ''} client
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-tp-text">Qualité du travail</span>
                      <span className="font-medium text-tp-text">{rating.quality_average.toFixed(1)}/5</span>
                    </div>
                    <Progress value={rating.quality_average * 20} className="h-2" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-tp-text">Communication</span>
                      <span className="font-medium text-tp-text">{rating.communication_average.toFixed(1)}/5</span>
                    </div>
                    <Progress value={rating.communication_average * 20} className="h-2" />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-tp-text">Respect des délais</span>
                      <span className="font-medium text-tp-text">{rating.deadline_average.toFixed(1)}/5</span>
                    </div>
                    <Progress value={rating.deadline_average * 20} className="h-2" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-tp-text">Recommandation</span>
                      <span className="font-medium text-tp-text">{rating.recommendation_average.toFixed(1)}/5</span>
                    </div>
                    <Progress value={rating.recommendation_average * 20} className="h-2" />
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-8 text-center border-tp-border">
              <Star className="h-12 w-12 text-tp-text-light/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-tp-text mb-2">Aucune évaluation</h3>
              <p className="text-sm text-tp-text-light">
                Ce talent n'a pas encore été évalué par un client.
              </p>
            </Card>
          )}
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="space-y-6">
          <Card className="p-6 border-tp-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-tp-accent" />
                <h3 className="text-lg font-display font-semibold text-tp-text">Notes internes</h3>
              </div>
              <Button 
                onClick={saveNotes} 
                disabled={savingNotes}
                className="bg-tp-primary hover:bg-tp-primary-light"
              >
                {savingNotes ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Sauvegarder
              </Button>
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajoutez des notes internes sur ce talent..."
              rows={8}
              className="resize-none"
            />
            <p className="text-xs text-tp-text-light mt-2">
              Ces notes sont visibles uniquement par l'équipe interne.
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeProfilePage;
